import React, { useState, useRef, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { BiCheckCircle } from "react-icons/bi";
import {
  postCustomerForm,
} from "../erpClient";
import toast from "react-hot-toast";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { useIndiaStateCity } from "../utils/useIndiaStateCity";
import {
  sanitizeMobileInput,
  isValidMobileNumber,
} from "../utils/mobileInput";
import { useMobileExistsCheck } from "../utils/useMobileExistsCheck";
import MobileExistsFeedback from "../components/MobileExistsFeedback";
import {
  buildMobileSelectOptions,
  buildNameSelectOptions,
  customerSelectMenuProps,
  fetchCustomerUserSearch,
} from "../utils/customerUserSelectOptions";


function TestCustomerForm() {
  const { buttonClass, cardClass, buttonTableStyle } = useButtonTableStyle();
  // State variables for form fields
  const [fullName, setFullName] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [state, setState] = useState("Gujarat");
  const [purposeOfVisit, setPurposeOfVisit] = useState("");
  const [noOfVisitors, setNoOfVisitors] = useState(1);
  const [functionPurpose, setFunctionPurpose] = useState("");
  const [functionDate, setFunctionDate] = useState("");
  const [scheme, setScheme] = useState("No");
  const [instagramFollow, setInstagramFollow] = useState("No");
  const [reference, setReference] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loadingMobileSearch, setLoadingMobileSearch] = useState(false);
  const [loadingNameSearch, setLoadingNameSearch] = useState(false);
  const [mobileOptions, setMobileOptions] = useState([]);
  const [nameOptions, setNameOptions] = useState([]);
  const [selectedMobile, setSelectedMobile] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [mobileInputValue, setMobileInputValue] = useState("");
  const [nameInputValue, setNameInputValue] = useState("");
  const mobileSearchTimerRef = useRef(null);
  const nameSearchTimerRef = useRef(null);

  const { states, selectedState, setSelectedState } = useIndiaStateCity("GJ");

  const stateOptions = states.map((s) => ({
    label: s.name,
    value: s.iso2,
  }));

  useEffect(() => {
    const gujarat = states.find((s) => s.name === "Gujarat");
    if (gujarat) {
      setState(gujarat.name);
      setSelectedState(gujarat.iso2);
    }
  }, [states, setSelectedState]);

  useEffect(() => {
    return () => {
      clearTimeout(mobileSearchTimerRef.current);
      clearTimeout(nameSearchTimerRef.current);
    };
  }, []);

  const isExistingCustomerSelected =
    (selectedMobile && !selectedMobile.__isNew__) ||
    (selectedName && !selectedName.__isNew__ && selectedName.mobile);

  const mobileExistsCheck = useMobileExistsCheck(
    mobileInputValue || mobileNo,
    {
      enabled:
        !isExistingCustomerSelected &&
        (!selectedMobile || selectedMobile.__isNew__),
    },
  );

  const scheduleMobileSearch = (rawInput) => {
    clearTimeout(mobileSearchTimerRef.current);
    const sanitized = sanitizeMobileInput(rawInput);
    setMobileInputValue(sanitized);
    if (!sanitized.trim()) {
      setMobileOptions([]);
      setLoadingMobileSearch(false);
      return;
    }
    setLoadingMobileSearch(true);
    mobileSearchTimerRef.current = setTimeout(async () => {
      try {
        const rows = await fetchCustomerUserSearch(sanitized);
        setMobileOptions(buildMobileSelectOptions(rows, sanitized));
      } catch {
        setMobileOptions([]);
      } finally {
        setLoadingMobileSearch(false);
      }
    }, 400);
  };

  const scheduleNameSearch = (rawInput) => {
    clearTimeout(nameSearchTimerRef.current);
    setNameInputValue(rawInput);
    if (!String(rawInput ?? "").trim()) {
      setNameOptions([]);
      setLoadingNameSearch(false);
      return;
    }
    setLoadingNameSearch(true);
    nameSearchTimerRef.current = setTimeout(async () => {
      try {
        const rows = await fetchCustomerUserSearch(rawInput);
        setNameOptions(buildNameSelectOptions(rows, rawInput));
      } catch {
        setNameOptions([]);
      } finally {
        setLoadingNameSearch(false);
      }
    }, 400);
  };

  // Handle Mobile Select/Creation
  const handleMobileChange = (option) => {
    const sanitizedOption = option
      ? { ...option, value: sanitizeMobileInput(option.value), label: sanitizeMobileInput(option.value) }
      : null;
    setSelectedMobile(sanitizedOption);
    setMobileNo(sanitizedOption ? sanitizedOption.value : "");
    setMobileInputValue("");
    // If exists, auto-select name
    if (option && option.name) {
      setSelectedName({
        value: option.name,
        label: option.name,
        mobile: sanitizedOption?.value || "",
      });
      setFullName(option.name);
    } else if (option && option.__isNew__) {
      // Only clear name if it is empty (preserve if user already typed it)
      if (!fullName) {
        setSelectedName(null);
        setFullName("");
      }
    } else {
      setSelectedName(null);
      setFullName("");
    }
  };

  // Handle Name Select/Creation
  const handleNameChange = (option) => {
    setSelectedName(option);
    setFullName(option ? option.value : "");
    // If exists, auto-select mobile
    if (option && option.mobile) {
      setSelectedMobile({
        value: option.mobile,
        label: option.mobile,
        name: option.value,
      });
      setMobileNo(option.mobile);
    } else if (option && option.__isNew__) {
      // Only clear mobile if it is empty (preserve if user already typed it)
      if (!mobileNo) {
        setSelectedMobile(null);
        setMobileNo("");
      }
    } else {
      setSelectedMobile(null);
      setMobileNo("");
    }
  };
  const handlePurposeOfVisitChange = (e) => setPurposeOfVisit(e.target.value);
  const handleNoOfVisitorsChange = (e) => setNoOfVisitors(e.target.value);
  const handleFunctionPurposeChange = (e) => setFunctionPurpose(e.target.value);
  const handleFunctionDateChange = (e) => setFunctionDate(e.target.value);
  const handleSchemeChange = (e) => setScheme(e.target.value);
  const handleInstagramFollowChange = (e) => setInstagramFollow(e.target.value);
  const handleReferenceChange = (e) => setReference(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!fullName.trim()) {
      toast.error("Please enter your full name");
      return;
    }

    if (!isValidMobileNumber(mobileNo)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    if (mobileExistsCheck.exists && !isExistingCustomerSelected) {
      toast(
        "This mobile is already registered — walk-in will be linked to the existing customer.",
        { icon: "ℹ️" },
      );
    }

    // Prepare request data
    const requestData = {
      type: "create",
      name: fullName,
      mobile: mobileNo,
      state: state || undefined,
      purpose_of_visit: purposeOfVisit,
      no_of_visitors: noOfVisitors,
      function_purpose: functionPurpose,
      function_date: functionDate,
      scheme: scheme,
      instagram: instagramFollow,
      about: reference,
      // by_user: userData.id || "",
    };
    // console.log("Request Data:", requestData);

    try {
      await postCustomerForm(requestData);
      // console.log("Response from API:", response.data);

      // Show success toast with response message
      toast.success("Form submitted successfully!");

      setShowAddModal(false);

      // Reset form fields
      setFullName("");
      setMobileNo("");
      setSelectedMobile(null);
      setSelectedName(null);
      setMobileInputValue("");
      setNameInputValue("");
      setMobileOptions([]);
      setNameOptions([]);
      const gujarat = states.find((s) => s.name === "Gujarat");
      if (gujarat) {
        setState(gujarat.name);
        setSelectedState(gujarat.iso2);
      } else {
        setState("Gujarat");
        setSelectedState("GJ");
      }
      setPurposeOfVisit("");
      setNoOfVisitors(1);
      setFunctionPurpose("");
      setFunctionDate("");
      setScheme("No");
      setInstagramFollow("No");
      setReference("");
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const selectStyles = {
    option: (base, state) => {
      const isCreateOption = state.data && state.data.__isNew__;
      const isHighlighted = state.isSelected || isCreateOption;
      return {
        ...base,
        fontSize: ".875rem",
        backgroundColor: isHighlighted
          ? "var(--theme-primary)"
          : state.isFocused
            ? "rgba(var(--bs-primary-rgb, 59, 130, 246), 0.1)"
            : "#ffffff",
        color: isHighlighted ? "#ffffff" : "var(--bs-heading-color)",
        fontWeight: isHighlighted ? 600 : 400,
        ":active": {
          backgroundColor: "var(--theme-primary)",
          color: "#ffffff",
        },
      };
    },
    control: (base, state) => ({
      ...base,
      minHeight: "44px",
      borderRadius: buttonTableStyle === "square" ? 0 : "8px",
      borderWidth: "1px",
      borderColor: state.isFocused ? "var(--theme-primary)" : "#e0e6eb",
      backgroundColor: "transparent",
      boxShadow: "none",
      fontSize: ".875rem",
      color: "var(--bs-heading-color)",
      "&:hover": {
        borderColor: state.isFocused ? "var(--theme-primary)" : "#cfd6db",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 16px",
      minHeight: "44px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--bs-heading-color)",
      fontSize: ".875rem",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--bs-heading-color)",
      opacity: 0.65,
      fontSize: ".875rem",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "44px",
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? "var(--theme-primary)" : "#b8c0c6",
      padding: "0 12px 0 8px",
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: "#e0e6eb",
      margin: "8px 0",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: buttonTableStyle === "square" ? 0 : "8px",
      border: "1px solid #e0e6eb",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
    }),
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Walk-In Customers</h4>
          </div>

          <div className="card-body p-4">
            <form className="walkin-form" onSubmit={handleSubmit}>
              {/* Row 1 */}
              <div className="row mb-3">
                <div className="col-md">
                  <label htmlFor="mobileNo" className="form-label">
                    Mobile No <span className="text-danger">*</span>
                  </label>
                  <CreatableSelect
                    id="mobileNo"
                    isClearable
                    isLoading={loadingMobileSearch}
                    options={mobileOptions}
                    value={selectedMobile}
                    inputValue={mobileInputValue}
                    {...customerSelectMenuProps}
                    onInputChange={(inputValue, { action }) => {
                      if (action === "input-change") {
                        scheduleMobileSearch(inputValue);
                      }
                      if (action === "menu-close" || action === "set-value") {
                        clearTimeout(mobileSearchTimerRef.current);
                        setMobileInputValue("");
                        setMobileOptions([]);
                        setLoadingMobileSearch(false);
                      }
                    }}
                    onChange={handleMobileChange}
                    placeholder="Type or select Mobile No"
                    formatCreateLabel={(inputValue) => `Add new: ${inputValue}`}
                    styles={{
                      ...selectStyles,
                      control: (base, state) => ({
                        ...selectStyles.control(base, state),
                        borderColor:
                          mobileExistsCheck.exists && !isExistingCustomerSelected
                            ? "#ffc107"
                            : state.isFocused
                              ? "var(--theme-primary)"
                              : "#e0e6eb",
                      }),
                    }}
                    classNamePrefix="walkin-select"
                  />
                  <MobileExistsFeedback
                    exists={mobileExistsCheck.exists && !isExistingCustomerSelected}
                    checking={mobileExistsCheck.checking}
                    message="Mobile already registered — you can still submit walk-in"
                    className={
                      mobileExistsCheck.exists && !isExistingCustomerSelected
                        ? "text-warning"
                        : ""
                    }
                  />
                </div>
                <div className="col-md">
                  <label htmlFor="fullName" className="form-label">
                    Full Name <span className="text-danger">*</span>
                  </label>
                  <CreatableSelect
                    id="fullName"
                    isClearable
                    isLoading={loadingNameSearch}
                    options={nameOptions}
                    value={selectedName}
                    inputValue={nameInputValue}
                    {...customerSelectMenuProps}
                    onInputChange={(inputValue, { action }) => {
                      if (action === "input-change") {
                        scheduleNameSearch(inputValue);
                      }
                      if (action === "menu-close" || action === "set-value") {
                        clearTimeout(nameSearchTimerRef.current);
                        setNameInputValue("");
                        setNameOptions([]);
                        setLoadingNameSearch(false);
                      }
                    }}
                    onChange={handleNameChange}
                    placeholder="Type or select Full Name"
                    formatCreateLabel={(inputValue) => `Add new: ${inputValue}`}
                    styles={selectStyles}
                    classNamePrefix="walkin-select"
                  />
                </div>
                <div className="col-md">
                  <label htmlFor="stateSelect" className="form-label">
                    State <span className="text-danger">*</span>
                  </label>
                  <Select
                    id="stateSelect"
                    options={stateOptions}
                    value={
                      selectedState
                        ? { label: state, value: selectedState }
                        : null
                    }
                    onChange={(option) => {
                      setSelectedState(option.value);
                      setState(option.label);
                    }}
                    styles={selectStyles}
                    placeholder="Select State"
                    classNamePrefix="walkin-select"
                  />
                </div>
                <div className="col-md">
                  <label htmlFor="purposeOfVisit" className="form-label">
                    Purpose of Visit
                  </label>
                  <input
                    type="text"
                    className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                      }`}
                    id="purposeOfVisit"
                    placeholder="e.g. looking for gold chain"
                    value={purposeOfVisit}
                    onChange={handlePurposeOfVisitChange}
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <label htmlFor="functionPurpose" className="form-label">
                    Function Purpose
                  </label>
                  <input
                    type="text"
                    className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                      }`}
                    id="functionPurpose"
                    placeholder="e.g. Wedding, Social Gathering"
                    value={functionPurpose}
                    onChange={handleFunctionPurposeChange}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="functionDate" className="form-label">
                    Function Date
                  </label>
                  <input
                    type="date"
                    className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                      }`}
                    id="functionDate"
                    value={functionDate}
                    onChange={handleFunctionDateChange}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="noOfVisitors" className="form-label">
                    No of Visitors
                  </label>
                  <input
                    type="number"
                    className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                      }`}
                    id="noOfVisitors"
                    value={noOfVisitors}
                    onChange={handleNoOfVisitorsChange}
                    min="1"
                  />
                </div>
              </div>

              {/* Row 3 — reference + scheme + Instagram in one line */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label">
                    How did you hear about us?
                  </label>
                  <div>
                    <div className="form-check mb-2">
                      <input
                        type="radio"
                        id="referenceRelativeFriends"
                        name="reference"
                        value="Relative/Friends"
                        checked={reference === "Relative/Friends"}
                        onChange={handleReferenceChange}
                        className="form-check-input"
                      />
                      <label
                        htmlFor="referenceRelativeFriends"
                        className="form-check-label"
                      >
                        Relative/Friends
                      </label>
                    </div>
                    <div className="form-check mb-2">
                      <input
                        type="radio"
                        id="referenceSocialMedia"
                        name="reference"
                        value="Social media"
                        checked={reference === "Social media"}
                        onChange={handleReferenceChange}
                        className="form-check-input"
                      />
                      <label
                        htmlFor="referenceSocialMedia"
                        className="form-check-label"
                      >
                        Social media
                      </label>
                    </div>
                    <div className="form-check mb-2">
                      <input
                        type="radio"
                        id="referenceYoutube"
                        name="reference"
                        value="Youtube"
                        checked={reference === "Youtube"}
                        onChange={handleReferenceChange}
                        className="form-check-input"
                      />
                      <label
                        htmlFor="referenceYoutube"
                        className="form-check-label"
                      >
                        Youtube
                      </label>
                    </div>
                    <div className="form-check mb-2">
                      <input
                        type="radio"
                        id="referenceHoldings"
                        name="reference"
                        value="Holdings"
                        checked={reference === "Holdings"}
                        onChange={handleReferenceChange}
                        className="form-check-input"
                      />
                      <label
                        htmlFor="referenceHoldings"
                        className="form-check-label"
                      >
                        Holdings
                      </label>
                    </div>
                    <div className="form-check mb-2">
                      <input
                        type="radio"
                        id="referenceAutoRickshawAd"
                        name="reference"
                        value="Autorickshaw ad"
                        checked={reference === "Autorickshaw ad"}
                        onChange={handleReferenceChange}
                        className="form-check-input"
                      />
                      <label
                        htmlFor="referenceAutoRickshawAd"
                        className="form-check-label"
                      >
                        Autorickshaw ad
                      </label>
                    </div>
                    <div className="form-check mb-2">
                      <input
                        type="radio"
                        id="referenceNewspaperTemplate"
                        name="reference"
                        value="Newspaper/Template"
                        checked={reference === "Newspaper/Template"}
                        onChange={handleReferenceChange}
                        className="form-check-input"
                      />
                      <label
                        htmlFor="referenceNewspaperTemplate"
                        className="form-check-label"
                      >
                        Newspaper/Template
                      </label>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Do you know about the scheme?
                  </label>
                  <div>
                    <div className="form-check mb-2">
                      <input
                        type="radio"
                        id="schemeYes"
                        name="scheme"
                        value="Yes"
                        checked={scheme === "Yes"}
                        onChange={handleSchemeChange}
                        className="form-check-input"
                      />
                      <label htmlFor="schemeYes" className="form-check-label">
                        Yes
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="schemeNo"
                        name="scheme"
                        value="No"
                        checked={scheme === "No"}
                        onChange={handleSchemeChange}
                        className="form-check-input"
                      />
                      <label htmlFor="schemeNo" className="form-check-label">
                        No
                      </label>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Do you follow us on Instagram?
                  </label>
                  <div>
                    <div className="form-check mb-2">
                      <input
                        type="radio"
                        id="instagramFollowYes"
                        name="instagramFollow"
                        value="Yes"
                        checked={instagramFollow === "Yes"}
                        onChange={handleInstagramFollowChange}
                        className="form-check-input"
                      />
                      <label
                        htmlFor="instagramFollowYes"
                        className="form-check-label"
                      >
                        Yes
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="instagramFollowNo"
                        name="instagramFollow"
                        value="No"
                        checked={instagramFollow === "No"}
                        onChange={handleInstagramFollowChange}
                        className="form-check-input"
                      />
                      <label
                        htmlFor="instagramFollowNo"
                        className="form-check-label"
                      >
                        No
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="d-flex justify-content-end">
                <button
                  type="submit"
                  className={`btn btn-primary ${buttonClass}`}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Success Modal */}
      <Modal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        centered
        contentClassName={buttonTableStyle === "square" ? "rounded-0" : ""}
      >
        <Modal.Body className="text-center">
          <div className="d-flex flex-column align-items-center">
            <BiCheckCircle size={60} color="green" />
            <h5 className="mt-3">Successfully Submitted</h5>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default TestCustomerForm;
